import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { Locales, messageToString, PrimaryAnchor } from '@ratehub/base-ui';

import Paths from '../definitions/Paths';
import HeroTabContentListItemContainer from './HeroTabContentListItemContainer';


function HeroTabContentCreditCards() {
    const intl = useIntl();

    const ctaLink = intl.locale === Locales.FRENCH
        ? (Paths.FIND_PERFECT_CARD_FR ?? Paths.FIND_PERFECT_CARD_EN)
        : Paths.FIND_PERFECT_CARD_EN;

    return (
        <Container className="rh-display-grid rh-gap-1">
            {/* Column 1 */}
            <div className="rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-display-flex rh-flex-direction-column rh-gap-1">
                <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentCreditCards.col1.title"
                        defaultMessage="Find your perfect card"
                    />
                </h2>
                <HeroTabContentListItemContainer
                    listItems={listItems}
                />
            </div>

            {/* Column 2 */}
            <div className="col-2 rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-text-align-center rh-display-flex rh-flex-direction-column rh-justify-content-space-between rh-gap-1">
                <div className="image-wrapper rh-position-relative rh-width-100p rh-height-100p">
                    <img
                        // Placeholder image until we have a proper design
                        src="https://media.istockphoto.com/id/2182520744/photo/wooden-hut-is-the-best-place-to-have-an-autumn-vacation.jpg?s=2048x2048&w=is&k=20&c=4-ie1gFMjkSUqpufJl9r73EYaPSAf-0ijq8rTgokanY="
                        alt={messageToString(
                            <FormattedMessage
                                id="web-components.HeroTabContentCreditCards.img.alt"
                                defaultMessage="Young woman using a digital tablet on a sunny autumn day"
                            />,
                            intl,
                        )}
                        className="rh-position-absolute"
                    />
                </div>
                <div className="cta-section">
                    <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentCreditCards.col2.title"
                            defaultMessage="CardFinder tool"
                        />
                    </h2>
                    <p className="rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentCreditCards.col2.subtitle"
                            defaultMessage="Instant credit card matches — see what you qualify for in under a minute."
                        />
                    </p>
                    <PrimaryAnchor
                        href={ctaLink}
                        size="medium"
                        className="rh-mt-1"
                        data-name="hero-tab-content-card-finder-link"
                    >
                        <FormattedMessage
                            id="web-components.HeroTabContentCreditCards.col2.button"
                            defaultMessage="Find my perfect card"
                        />
                    </PrimaryAnchor>
                </div>
            </div>
        </Container>
    );
}

const Container = styled.div`
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-template-rows: auto;

    > .col-2 {
        > .image-wrapper {
            min-height: 15rem;

            > img {
                max-width: 100%;
                max-height: 100%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                object-fit: cover;
                border-radius: 100vh;
                aspect-ratio: 1/1;
            }
        }

        > .cta-section {
            flex: 1 1 auto;
        }
    }
`;

const listItems = [
    {
        iconKey: 'suitcase',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.travel.title"
                defaultMessage="Travel"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.travel.subtitle"
                defaultMessage="Earn rewards and perks"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.BEST_TRAVEL_CARDS_EN,
            [Locales.FRENCH]: Paths.BEST_TRAVEL_CARDS_FR,
        },
    },
    {
        iconKey: 'refresh',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.balanceTransfer.title"
                defaultMessage="Balance transfer"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.balanceTransfer.subtitle"
                defaultMessage="Transfer at 0% interest"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.BALANCE_TRANSFER_CARDS_EN,
            [Locales.FRENCH]: Paths.BALANCE_TRANSFER_CARDS_FR,
        },
    },
    {
        iconKey: 'magic-wand',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.bestOverall.title"
                defaultMessage="Best overall credit cards"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.bestOverall.subtitle"
                defaultMessage="Compare top cards"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.BEST_OVERALL_CARDS_EN,
            [Locales.FRENCH]: Paths.BEST_OVERALL_CARDS_FR,
        },
    },
    {
        iconKey: 'credit-card-pop',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.topPromotions.title"
                defaultMessage="Top credit card promotions"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentCreditCards.listItems.topPromotions.subtitle"
                defaultMessage="Discover exclusive offers"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.TOP_PROMOTIONS_CARDS_EN,
            [Locales.FRENCH]: Paths.TOP_PROMOTIONS_CARDS_FR,
        },
    },
];

export default HeroTabContentCreditCards;
